import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Box } from '@frontierag/frontier-ui-library';
import useSize from 'hooks/useSize';
import { useDebouncedCallback } from 'use-debounce';

import PageLayoutContext from './PageLayoutContext';
import type { IPageLayoutProvider } from './types';

export default function PageLayoutProvider({ children, containerRef }: IPageLayoutProvider) {
  const contentRef = useRef<HTMLDivElement>();
  const sizes = useSize(contentRef);

  const target = containerRef && containerRef.current ? containerRef.current : null;
  const [scrollSizes, setScrollSizes] = useState<object>({
    scrollTop: 0,
    offsetHeight: 0,
    scrollHeight: 0,
  });

  const handleScrollChanges = useDebouncedCallback(() => {
    if (target) {
      setScrollSizes({
        scrollTop: target.scrollTop,
        offsetHeight: target.offsetHeight,
        scrollHeight: target.scrollHeight,
      });
    }
  }, 200);

  useLayoutEffect(() => {
    handleScrollChanges();
  }, [handleScrollChanges]);

  useEffect(() => {
    const handleScroll = () => handleScrollChanges();
    if (target) {
      target.addEventListener('scroll', handleScroll);

      return () => target.removeEventListener('scroll', handleScroll);
    }
  });

  useEffect(() => {
    const handleResize = () => handleScrollChanges();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  });

  return (
    <PageLayoutContext.Provider
      value={{
        sizes,
        scrollSizes: {
          ...scrollSizes,
          scrollTop: target?.scrollTop,
          offsetHeight: target?.offsetHeight,
          scrollHeight: target?.scrollHeight,
        },
      }}
    >
      <Box ref={contentRef} display="flex" flexDirection="column" flexGrow={1}>
        {children}
      </Box>
    </PageLayoutContext.Provider>
  );
}
